import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  MegaphoneIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@tremor/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../services/AuthProvider";
import ThemeToggle, { ThemeMenuItems } from "./ThemeToggle";

const baseNavigation = [
  { name: "Devices", href: "/devices" },
  { name: "Dashboards", href: "/dashboards" },
  { name: "Profiles", href: "/profiles" },
  { name: "Sources", href: "/sources" },
  { name: "Integrations", href: "/integrations", premium: true },
  { name: "Docs", href: "https://zafron.dev/docs/intro", external: true },
];

const adminNavigation = [
  { name: "Admin", href: "/admin" }
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Navbar({ user }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout } = useAuth();

  const isActive = (href) => pathname === href || pathname.startsWith(href + '/');

  // Filter navigation based on user's premium status
  const navigation = baseNavigation.filter(item => {
    // If item requires premium and user is not premium, hide it
    if (item.premium && (!user?.isPremium)) {
      return false;
    }
    return true;
  });

  const signIn = () => {};
  const signOut = () => {
    logout();
    navigate("/login");
  };
  const accountClick = () => {
    navigate("/account");
  };

  return (
    <Disclosure as="nav" className="bg-white dark:bg-gray-900 shadow-sm">
      {({ open }) => (
        <>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 justify-between">
              <div className="flex">
                <div className="flex flex-shrink-0 items-center">
                  <a href="/devices" className="cursor-pointer">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="0" y="0" width="40" height="40" rx="9" ry="9" fill="#2563eb"/>
                      <text x="20" y="28" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="300" fontSize="22" fill="#ffffff">Z</text>
                    </svg>
                  </a>
                </div>
                <div className="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
                  {navigation.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className={classNames(
                        isActive(item.href)
                          ? "border-slate-500 text-gray-900 dark:text-gray-50"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-700",
                        "inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium",
                      )}
                      aria-current={isActive(item.href) ? "page" : undefined}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                    >
                      {item.name}
                    </a>
                  ))}
                  {user.isAdmin &&
                    adminNavigation.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className={classNames(
                          isActive(item.href)
                            ? "border-slate-500 text-gray-900 dark:text-gray-50"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-700",
                          "inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium",
                        )}
                        aria-current={
                          isActive(item.href) ? "page" : undefined
                        }
                        target={item.external ? "_blank" : undefined}
                        rel={item.external ? "noopener noreferrer" : undefined}
                      >
                        {item.name}
                      </a>
                    ))}
                </div>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:items-center">
                <div>
                  <a
                    data-canny-link
                    href="https://zafron.dev/blog"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MegaphoneIcon className="text-blue-500 w-7 h-7 mr-3" />
                  </a>
                </div>
                <Menu as="div" className="relative ml-3">
                  <div>
                    <MenuButton className="flex items-center rounded-full bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900">
                      <span className="sr-only">Open user menu</span>
                      <span className="relative">
                        <UserCircleIcon className="text-gray-400 dark:text-gray-500 w-10 h-10" />
                        {user?.isPremium && (
                          <span className="absolute -bottom-1 -right-1 inline-flex items-center rounded-full bg-blue-600 px-1 py-0.5 text-[9px] font-bold leading-none text-white ring-1 ring-white">
                            PRO
                          </span>
                        )}
                      </span>
                    </MenuButton>
                  </div>
                  <Transition
                    enter="transition ease-out duration-200"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <MenuItems className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-900 py-1 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-800 focus:outline-none">
                      <ThemeMenuItems />
                      <div className="my-1 border-t border-gray-100 dark:border-gray-800" />
                      {user ? (
                        <>
                          <MenuItem>
                            <button
                              onClick={() => accountClick()}
                              className="flex w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                            >
                              Settings
                            </button>
                          </MenuItem>
                          <MenuItem>
                            {({ active }) => (
                              <button
                                className={classNames(
                                  active ? "bg-gray-100 dark:bg-gray-800" : "",
                                  "flex w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300",
                                )}
                                onClick={() => signOut()}
                              >
                                Sign out
                              </button>
                            )}
                          </MenuItem>
                        </>
                      ) : (
                        <MenuItem>
                          {({ active }) => (
                            <button
                              className={classNames(
                                active ? "bg-gray-100 dark:bg-gray-800" : "",
                                "flex w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300",
                              )}
                              onClick={() => signIn("github")}
                            >
                              Sign in
                            </button>
                          )}
                        </MenuItem>
                      )}
                    </MenuItems>
                  </Transition>
                </Menu>
              </div>
              <div className="-mr-2 flex items-center sm:hidden">
                <DisclosureButton className="inline-flex items-center justify-center rounded-md bg-white dark:bg-gray-900 p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </DisclosureButton>
              </div>
            </div>
          </div>

          <DisclosurePanel className="sm:hidden">
            <div className="space-y-1 pt-2 pb-3">
              {navigation.map((item) => (
                <DisclosureButton
                  key={item.name}
                  as="a"
                  href={item.href}
                  className={classNames(
                    isActive(item.href)
                      ? "bg-slate-50 border-slate-500 text-slate-700 dark:bg-gray-800 dark:text-slate-300"
                      : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:border-gray-700 dark:hover:text-gray-200",
                    "block pl-3 pr-4 py-2 border-l-4 text-base font-medium",
                  )}
                  aria-current={isActive(item.href) ? "page" : undefined}
                >
                  {item.name}
                </DisclosureButton>
              ))}
              {user.isAdmin &&
                adminNavigation.map((item) => (
                  <DisclosureButton
                    key={item.name}
                    as="a"
                    href={item.href}
                    className={classNames(
                      isActive(item.href)
                        ? "bg-slate-50 border-slate-500 text-slate-700 dark:bg-gray-800 dark:text-slate-300"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:border-gray-700 dark:hover:text-gray-200",
                      "block pl-3 pr-4 py-2 border-l-4 text-base font-medium",
                    )}
                    aria-current={isActive(item.href) ? "page" : undefined}
                  >
                    {item.name}
                  </DisclosureButton>
                ))}
            </div>
            <div className="border-t border-gray-200 dark:border-gray-800 pt-4 pb-3">
              {user ? (
                <>
                  <div className="flex items-center px-4">
                    <div className="ml-3">
                      <div className="text-base font-medium text-gray-800 dark:text-gray-100">
                        {user.name}
                      </div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 px-4">
                    <ThemeToggle />
                  </div>
                  <div className="mt-3 pb-3 space-y-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => signOut()}
                    >
                      Sign out
                    </Button>
                  </div>
                </>
              ) : (
                <div className="mt-3 space-y-1">
                  <button
                    onClick={() => signIn("login")}
                    className="flex w-full px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  >
                    Sign in
                  </button>
                </div>
              )}
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}
